import React, { useRef, useState } from "react";
import { OutsideClickHandler } from "components/OutsideClickHandler";
import { RecentSearchCardV2 } from "components/V2/atoms/RecentSearchesCardV2/RecentSearchesCardV2";
import { TextFieldV2 } from "components/V2/atoms/TextFieldV2/TextFieldV2";
import { extractResource } from "helper/extractResource";
import { useSelector } from "react-redux";
import ShimmerLoader from "../../atoms/ShimmerLoaderV2/ShimmerLoaderV2";
import { AirportsInformation } from "../ManageBookings/RetrieveBookingWithMobileFormV2/RetrieveBookingWithMobileFormV2";

interface CityAutocompleteTextboxV2Props {
  labelText: string;
  value: string;
  onChange: (value: string) => void;
  suggestions: [];
  onItemClick: (item: any) => void;
  disabled?: boolean;
  errorText: string;
  onClickRecentSearch?: (item?: any) => void;
  resourceData?: {};
  showRecent?: boolean;
}

export function CityAutocompleteTextboxV2({
  labelText,
  value,
  onChange,
  suggestions,
  onItemClick,
  disabled = false,
  errorText,
  onClickRecentSearch,
  resourceData,
  showRecent = false
}: CityAutocompleteTextboxV2Props) {
  const [open, setOpen] = useState(false);
  const [destinationBoxHeight, setDestinationBoxHeight] = useState<number>(0);

  const storedSearches = useSelector((state) => state.selectWebConfigState);

  const destinationContainer = useRef<HTMLDivElement | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onChange(inputValue);
  };
  const handleSuggestionClick = (item: AirportsInformation) => {
    onItemClick(item);
    setOpen(false);
  };

  const handleClosePopper = () => {
    setOpen(false);
  };

  const handleOpenPopper = () => {
    calculateParentHeight();
    setOpen(true);
  };

  const handleRecentSearchClick = (item: any) => {
    setOpen(false);
    if (onClickRecentSearch) {
      onClickRecentSearch(item);
    }
  };

  const calculateParentHeight = () => {
    if (destinationContainer?.current) {
      setDestinationBoxHeight(destinationContainer?.current?.clientHeight);
    }
  };

  return (
    <div>
      <TextFieldV2
        value={value}
        onClick={handleOpenPopper}
        onFocus={handleOpenPopper}
        onChange={handleInputChange}
        autoComplete="off"
        additionalInputClassNames="min-w-[206px] text-sm font-medium text-charcoal "
        name={labelText}
        additionalLabelClassNames="text-sm"
        disabled={disabled}
        label={labelText}
        errorText={errorText}
        legend={open}
        required
      />

      {open && (
        <OutsideClickHandler onOutsideClick={handleClosePopper}>
          <div className={` bg-white border border-form-border-color dark:bg-gray-900 shadow-md rounded-lg h-[52vh] absolute z-10 ${suggestions.length !== 0 ? "overflow-y-scroll" : "overflow-y-hidden"} overflow-x-hidden mt-2 `}>
            {showRecent && storedSearches && storedSearches.length !== 0 ? (
              <div className="bg-gray-100 dark:bg-gray-800 p-4">
                <div className="text-sm font-semibold">{extractResource(resourceData, "recentStationSearches")}</div>
                <div className="flex justify-left p-3 flex-wrap gap-3">
                  {Array.isArray(storedSearches) &&
                    storedSearches?.map((item: any) => (
                      <RecentSearchCardV2
                        origin={item?.origin}
                        destination={item?.destination}
                        type={item?.type}
                        handleOnClick={() => handleRecentSearchClick(item)}
                        key={`${item?.origin?.stationCode?.toLowerCase()}-${item?.destination?.stationCode.toLowerCase()}`}
                      />
                    ))}
                </div>
              </div>
            ) : null}
            <h3 className="text-sm font-semibold px-4 pt-4 text-typography-primary">Our Destinations</h3>
            {suggestions && suggestions.length > 0 ? (
              <ul className="p-4" id="destinations">
                {suggestions.map(
                  (item: AirportsInformation) =>
                    item.stationCode &&
                    item.shortName &&
                    item.fullName &&
                    item.cityInfo.name && (
                      <li
                        id={item?.stationCode}
                        role="presentation"
                        key={item?.stationCode}
                        onClick={() => handleSuggestionClick(item)}
                        className="flex justify-start gap-4 border-b-[1px] dark:bg-gray-900 border-gray-300 py-2 px-2 cursor-pointer hover:bg-slate-50	"
                      >
                        <div
                          role="presentation"
                          className="flex items-center justify-center w-20 p-2 dark:bg-gray-800 rounded-md text-base leading-6 font-medium bg-custom-bg-default text-custom-text-color shadow-md"
                        >
                          <span className="font-medium">{item?.stationCode}</span>
                        </div>
                        <div role="presentation">
                          <p className="font-medium text-sm">{item.cityInfo.name}</p>
                          <p className="text-xs dark:text-gray-400 text-gray-600">{item?.fullName}</p>
                        </div>
                      </li>
                    )
                )}
              </ul>
            ) : (
              <div ref={destinationContainer} className="h-[90%]">
                <ShimmerLoader
                  parentHeight={destinationBoxHeight}
                  containerClass="w-[450px] px-5 mt-4"
                  boxClass="h-[50px]"
                />
              </div>
            )}
          </div>
        </OutsideClickHandler>
      )}
    </div>
  );
}
